import React, { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, Expand, X, Images } from "lucide-react";
import { VehiclePlaceholder } from "@/components/VehicleImage";
import { imgSrc } from "@/lib/media";

// Phones get the 960px variant in the inline gallery; fullscreen and desktop use 1920px.
const MAIN_SIZE = typeof window !== "undefined" && window.matchMedia("(max-width: 1023px)").matches ? "card" : "large";

const useSwipe = (step) => {
  const start = useRef(null);
  return {
    onTouchStart: (e) => {
      const t = e.touches[0];
      start.current = e.touches.length === 1 ? { x: t.clientX, y: t.clientY } : null;
    },
    onTouchEnd: (e) => {
      const s = start.current;
      start.current = null;
      if (!s) return;
      const dx = e.changedTouches[0].clientX - s.x;
      const dy = e.changedTouches[0].clientY - s.y;
      if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.2) step(dx < 0 ? 1 : -1);
    },
    onTouchCancel: () => { start.current = null; },
  };
};

// Keeps the last loaded photo visible until the next one has loaded, then cross-fades.
const SmoothImage = ({ src, alt, fit, testId, onError, onClick, className = "" }) => {
  const [shown, setShown] = useState(null);
  const ready = shown === src;
  const base = `absolute inset-0 w-full h-full ${fit} select-none`;
  return (
    <>
      {shown && !ready && <img src={shown} alt="" aria-hidden="true" draggable={false} className={base} />}
      <img key={src} src={src} alt={alt} data-testid={testId} decoding="async" draggable={false}
        onLoad={() => setShown(src)} onError={onError} onClick={onClick}
        className={`${base} transition-opacity duration-300 ${ready ? "opacity-100" : "opacity-0"} ${className}`} />
      {!ready && (
        <span className="absolute left-1/2 top-1/2 -ml-5 -mt-5 p-2 rounded-full bg-black/45 backdrop-blur-sm fade-up pointer-events-none"
          style={{ animationDelay: "400ms" }} data-testid={`${testId}-loading`}>
          <span className="block w-6 h-6 rounded-full border-2 border-white/35 border-t-white animate-spin" />
        </span>
      )}
    </>
  );
};

const NavBtn = ({ dir, onClick, testId, dark }) => (
  <button data-testid={testId} onClick={onClick} aria-label={dir < 0 ? "Vorheriges Bild" : "Nächstes Bild"}
    className={`absolute top-1/2 -translate-y-1/2 ${dir < 0 ? "left-3 md:left-5" : "right-3 md:right-5"} rounded-full p-3 transition-colors ${
      dark ? "bg-white/10 text-white hover:bg-white/20" : "glass text-ink hover:bg-white"}`}>
    {dir < 0 ? <ChevronLeft size={18} /> : <ChevronRight size={18} />}
  </button>
);

const Thumbs = ({ imgs, idx, setIdx, dark, testPrefix }) => {
  const ref = useRef(null);
  useEffect(() => {
    const box = ref.current;
    const el = box?.children[idx];
    if (box && el) box.scrollTo({ left: el.offsetLeft - box.clientWidth / 2 + el.clientWidth / 2, behavior: "smooth" });
  }, [idx]);
  return (
    <div ref={ref} className="relative flex gap-2.5 overflow-x-auto no-scrollbar py-1 px-0.5" data-testid={`${testPrefix}-thumbs`}>
      {imgs.map((img, i) => (
        <button key={img} data-testid={`${testPrefix}-thumb-${i}`} onClick={() => setIdx(i)} aria-label={`Bild ${i + 1}`}
          className={`relative overflow-hidden rounded-xl shrink-0 w-[88px] h-[66px] md:w-24 md:h-[72px] transition-[opacity,box-shadow] duration-200 ${
            i === idx ? `ring-2 ${dark ? "ring-white" : "ring-ink"} opacity-100` : "opacity-55 hover:opacity-100"}`}>
          <img src={imgSrc(img, "thumb")} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />
        </button>
      ))}
    </div>
  );
};

const Fullscreen = ({ imgs, idx, setIdx, step, onClose, title }) => {
  const swipe = useSwipe(step);
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") step(1);
      if (e.key === "ArrowLeft") step(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose, step]);
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid="gallery-fullscreen-overlay"
      className="fixed inset-0 z-[80] bg-black flex flex-col overscroll-contain">
      <div className="flex items-center justify-between px-5 md:px-8 h-16 text-white/70 text-sm shrink-0">
        <span className="truncate pr-4">{title}</span>
        <span className="flex items-center gap-5">
          <span data-testid="gallery-fs-counter">{idx + 1} / {imgs.length}</span>
          <button data-testid="gallery-fullscreen-close" onClick={onClose} aria-label="Schließen" className="p-2 hover:text-white transition-colors"><X size={26} /></button>
        </span>
      </div>
      <div className="relative flex-1 min-h-0 touch-pinch-zoom" {...swipe}>
        <div className="absolute inset-0 mx-2 md:mx-20">
          <SmoothImage src={imgSrc(imgs[idx], "large")} alt={`${title} – Bild ${idx + 1}`} fit="object-contain" testId="gallery-fs-image" />
        </div>
        {imgs.length > 1 && (
          <>
            <NavBtn dir={-1} onClick={() => step(-1)} testId="gallery-fs-prev-btn" dark />
            <NavBtn dir={1} onClick={() => step(1)} testId="gallery-fs-next-btn" dark />
          </>
        )}
      </div>
      {imgs.length > 1 && (
        <div className="px-4 md:px-8 py-4 shrink-0 pb-[calc(1rem+env(safe-area-inset-bottom))]">
          <Thumbs imgs={imgs} idx={idx} setIdx={setIdx} dark testPrefix="gallery-fs" />
        </div>
      )}
    </motion.div>
  );
};

export const Gallery = ({ vehicle: v }) => {
  const [broken, setBroken] = useState(() => new Set());
  const imgs = (v.images || []).filter((u, i, all) => u && all.indexOf(u) === i && !broken.has(u));
  const n = imgs.length;
  const [idx, setIdx] = useState(0);
  const [full, setFull] = useState(false);
  const cur = Math.min(idx, Math.max(n - 1, 0));
  const step = useCallback((d) => n && setIdx((i) => (Math.min(i, n - 1) + d + n) % n), [n]);
  const close = useCallback(() => setFull(false), []);
  const swipe = useSwipe(step);

  useEffect(() => {
    if (n < 2) return;
    const size = full ? "large" : MAIN_SIZE;
    [1, -1, 2].forEach((d) => { new Image().src = imgSrc(imgs[(cur + d + n) % n], size); });
  }, [cur, n, full]); // eslint-disable-line react-hooks/exhaustive-deps

  const markBroken = (u) => setBroken((b) => new Set(b).add(u));

  return (
    <div data-testid="vehicle-gallery">
      <div className="relative overflow-hidden rounded-3xl bg-elevated aspect-[4/3] group card-soft touch-pan-y touch-pinch-zoom" {...swipe}>
        {n === 0 ? (
          <VehiclePlaceholder vehicle={v} size="large" />
        ) : (
          <>
            <SmoothImage src={imgSrc(imgs[cur], MAIN_SIZE)} alt={`${v.title} – Bild ${cur + 1}`} fit="object-cover" testId="gallery-main-image"
              onError={() => markBroken(imgs[cur])} onClick={() => setFull(true)} className="cursor-zoom-in" />
            <button data-testid="gallery-fullscreen-btn" onClick={() => setFull(true)} aria-label="Vollbild"
              className="absolute top-4 right-4 glass rounded-full p-3 text-ink md:opacity-0 md:group-hover:opacity-100 transition-opacity duration-300">
              <Expand size={17} />
            </button>
          </>
        )}
        {n > 1 && (
          <>
            <NavBtn dir={-1} onClick={() => step(-1)} testId="gallery-prev-btn" />
            <NavBtn dir={1} onClick={() => step(1)} testId="gallery-next-btn" />
            <button onClick={() => setFull(true)} data-testid="gallery-counter"
              className="absolute bottom-4 right-4 inline-flex items-center gap-1.5 rounded-full bg-black/55 backdrop-blur-md text-white text-xs font-semibold px-3 py-1.5">
              <Images size={13} /> {cur + 1} / {n}
            </button>
          </>
        )}
      </div>
      {n > 1 && <div className="mt-4"><Thumbs imgs={imgs} idx={cur} setIdx={setIdx} testPrefix="gallery" /></div>}
      <AnimatePresence>
        {full && n > 0 && <Fullscreen imgs={imgs} idx={cur} setIdx={setIdx} step={step} onClose={close} title={v.title} />}
      </AnimatePresence>
    </div>
  );
};

export default Gallery;
